import React, { useCallback, useEffect, useState, useRef } from "react";

import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import PlaceIcon from "@mui/icons-material/Place";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import Layout4 from "../../DataLayouts/Layout4";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { Box, MenuItem, TextField } from "@mui/material";
import {
  fetchLocationOwnership,
  fetchOrganizationLocation,
} from "../../../Apis/OrganizationLocation";
import {
  LOCATION_DEFAULT_COLUMNS,
  LOCATIONS_DATAGRID_KEY,
  mapSavedLocationColumns,
  resolveLocationApiField,
} from "./locationListConstants";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_location_id",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({
        field: resolveLocationApiField(field),
        operator,
        value: filterValue,
      });
    }
  });

  return filters;
};

function OrganizationLocationList() {
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(LOCATION_DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [ownershipTypeOptions, setOwnershipTypeOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  // Load personalized layout (same API/key as Personalize View)
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const datagridKey = `${LOCATIONS_DATAGRID_KEY}_${org.organization_id}`;
        const configRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            datagrid_key: datagridKey,
            organization_id: org.organization_id,
          }),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const serverCfg =
            configResponse?.datagrid?.datagrid_default_configuration;

          if (serverCfg?.columns?.length > 0) {
            setTableConfig(serverCfg);
            setConfigColumns(mapSavedLocationColumns(serverCfg.columns));
          } else {
            setTableConfig(null);
            setConfigColumns(LOCATION_DEFAULT_COLUMNS);
          }
        } else {
          setTableConfig(null);
          setConfigColumns(LOCATION_DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setTableConfig(null);
        setConfigColumns(LOCATION_DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load locations data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: resolveLocationApiField(
              searchParams.get("sortBy") || "organization_location_id"
            ),
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
          organization_location_ownership_type_id: searchParams.get(
            "organization_location_ownership_type_id"
          ),
          general_city_id: searchParams.get("general_city_id"),
          city: searchParams.get("city"),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/locations-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        const result = await response.json().catch(() => ({}));

        if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchOrganizationLocation(org.organization_id);
          data = oldData?.locations || [];
          setPaginationData(DEFAULT_PAGINATION);
        } else if (!response.ok || result.success === false) {
          setLocations([]);
          setPaginationData(DEFAULT_PAGINATION);
          throw new Error(result.message || `HTTP error! status: ${response.status}`);
        } else if (result.success && result.data) {
          data = result.data;

          if (result.pagination) {
            const pageSize =
              result.pagination.pageSize || result.pagination.limit || 10;
            setPaginationData({
              page:
                result.pagination.page ||
                Number.parseInt(searchParams.get("page"), 10) ||
                1,
              pageSize,
              total: result.pagination.total || 0,
              totalPages: result.pagination.totalPages || 1,
              hasNext: result.pagination.hasNext || false,
              hasPrev: result.pagination.hasPrev || false,
            });
          }
        } else {
          data = [];
          setPaginationData(DEFAULT_PAGINATION);
        }

        // Transform data - preserve existing transformation logic
        let b = data.map((item) => {
          const locationId =
            item.organization_location_id ?? item.id ?? null;
          return {
            ...item,
            organization_location_id: locationId,
            id: locationId,
            city_name: item?.city_name ?? "-",
            state_name: item?.state_name ?? "-",
            country_name: item?.country_name ?? "-",
            location_ownership_type_name:
              item?.location_ownership_type_name ?? "",
            location_latitude:
              item?.location_latitude == null ? "" : item?.location_latitude,
            location_longitude:
              item?.location_longitude == null
                ? ""
                : item?.location_longitude,
            postal_code: item?.postal_code == null ? "" : item?.postal_code,
            area_sq_ft: item?.area_sq_ft == null ? "" : item?.area_sq_ft,
            number_of_floors:
              item?.number_of_floors == null ? "" : item?.number_of_floors,
          };
        });
        setLocations(b);
      } catch (error) {
        if (error.name === "AbortError") {
          return; // Request was cancelled, ignore
        }
        console.error("Error fetching locations:", error);
        setLocations([]);
        setPaginationData(DEFAULT_PAGINATION);
        toast.error("Failed to load locations");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams, listRefreshKey]);

  useEffect(() => {
    if (!org?.organization_id) {
      setOwnershipTypeOptions([]);
      setCityOptions([]);
      return undefined;
    }
    let cancelled = false;
    Promise.all([
      fetchLocationOwnership(org.organization_id),
      fetchOrganizationLocation(org.organization_id),
    ])
      .then(([ownershipData, locationData]) => {
        if (cancelled) return;
        const ownershipPayload = ownershipData?.ownershiptypes;
        const ownershipRows = Array.isArray(ownershipPayload)
          ? ownershipPayload
          : ownershipPayload?.data || [];
        setOwnershipTypeOptions(
          ownershipRows
            .map((item) => ({
              id: item?.organization_location_ownership_type_id,
              name: String(item?.location_ownership_type_name || "").trim(),
            }))
            .filter((item) => item.id != null && item.name)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        const locationRows = Array.isArray(locationData?.locations)
          ? locationData.locations
          : [];
        const seen = new Set();
        setCityOptions(
          locationRows
            .map((item) => ({
              id: item?.general_city_id ?? item?.city?.general_city_id,
              name: String(item?.city?.city_name || item?.city_name || "").trim(),
            }))
            .filter((item) => item.id != null && item.name && item.name !== "-")
            .filter((item) => {
              const key = String(item.id);
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch(() => {
        if (!cancelled) {
          setOwnershipTypeOptions([]);
          setCityOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  const handleLocationToolbarChange = (param) => (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set(param, value);
    else next.delete(param);
    next.set("page", "1");
    setSearchParams(next);
  };

  const selectedOwnershipType = (() => {
    const raw = String(
      searchParams.get("organization_location_ownership_type_id") || ""
    ).trim();
    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
  })();
  const selectedCity = (() => {
    const raw = String(searchParams.get("general_city_id") || "").trim();
    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
  })();
  const selectSx = {
    width: "100%",
    "& .MuiSelect-select": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };

  const deletelocation = useCallback(
    async (idOrItem) => {
      const locationId =
        typeof idOrItem === "object" && idOrItem !== null
          ? idOrItem.organization_location_id ?? idOrItem.id
          : idOrItem;
      const parsedLocationId = Number(locationId);

      if (!Number.isInteger(parsedLocationId) || parsedLocationId <= 0) {
        toast.error("Invalid location selected for deletion.");
        return;
      }

      try {
        const org_id = org.organization_id;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/location/${parsedLocationId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          toast.success("Location deleted successfully");
          setLocations((prev) =>
            prev.filter(
              (item) =>
                Number(item.organization_location_id ?? item.id) !==
                parsedLocationId
            )
          );
          setPaginationData((prev) => ({
            ...prev,
            total: Math.max(0, (prev.total || 0) - 1),
          }));
          setListRefreshKey((key) => key + 1);
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            toast.error(
              errorData?.errors?.organization_location_id?.[0] ||
                errorData?.error ||
                "Failed to delete location"
            );
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete location");
      }
    },
    [org?.organization_id]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(`/organization/location/edit/${item.id}`);
    },

    [navigate]
  );



       const handleShow = useCallback(
    (item) => {
      navigate(`/organization/location/view/${item.id}`)
    },
    [navigate],
  )

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sortBy", resolveLocationApiField(field));
          next.set("sortOrder", direction);
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    (filtersOrUpdates) => {
      setSearchParams(
        (prev) => {
          const prevParams = new URLSearchParams(prev);
          const next = new URLSearchParams(prev);

          const buildFilterSignature = (params) =>
            [...params.keys()]
              .filter((k) => k.startsWith("filter_") || k === "search")
              .sort()
              .map((k) => `${k}=${params.get(k) ?? ""}`)
              .join("&");

          const prevSignature = buildFilterSignature(prevParams);

          if (Array.isArray(filtersOrUpdates)) {
            [...next.keys()]
              .filter((k) => k.startsWith("filter_"))
              .forEach((k) => next.delete(k));
            filtersOrUpdates.forEach((f) => {
              if (f?.field != null) {
                next.set(`filter_${f.field}`, f.value ?? "");
                if (f.operator) {
                  next.set(`filter_${f.field}_operator`, f.operator);
                }
              }
            });
          } else if (
            filtersOrUpdates &&
            typeof filtersOrUpdates === "object" &&
            filtersOrUpdates.searchQuery !== undefined
          ) {
            if (String(filtersOrUpdates.searchQuery).trim() !== "") {
              next.set("search", filtersOrUpdates.searchQuery.trim());
            } else {
              next.delete("search");
            }
          }

          const nextSignature = buildFilterSignature(next);
          if (prevSignature !== nextSignature) {
            next.set("page", "1");
          }

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={" Locations"}
        btnName={"Add Location"}
        add_action={"LOCATION_ADD"}
        Data={locations}
        delete_action={"LOCATION_DELETE"}
        tableHeaders={[
          {
            name: "Location ",
            value_key: "location_name",
            width: "130px",
            textStyle: "capitalize",
          },
          { name: "Address ", value_key: "addressline1", width: "200px" },
          {
            name: "Country",
            value_key: "general_country_id",
            width: "100px",
            textStyle: "capitalize",
          },
          {
            name: "State",
            value_key: "general_state_id",
            width: "130px",
            textStyle: "capitalize",
          },
          {
            name: "City",
            value_key: "general_city_id",
            width: "130px",
            textStyle: "capitalize",
          },
          { name: "Postal Code", value_key: "postal_code", width: "130px" },
        ]}
        Icons={[
          <AddLocationAltIcon
            sx={{ fontSize: 60, color: "grey.500", mb: 2 }}
          />,
          <PlaceIcon color="primary" />,
          <PlaceIcon color="primary" />,

          <LocationCityIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "locations",
          "organisation locations",
          "Add Location",
          "Location, Removing this location will delete its associated organizations and all related data permanently.",
        ]}
        Route={"/organization/location"}
        setData={setLocations}
        DeleteFunc={deletelocation}
      />

      <div className="ems-section-divider" />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_locations"
          primaryKey="organization_location_id"
          heading="Locations"
          data={locations}
          sortname="location_name"
          showActions={true}
          Route="/organization/location"
          DeleteFunc={deletelocation}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          edit_delete_action={["LOCATION_EDIT", "LOCATION_DELETE"]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="organization_locations"
          loadedBackendConfig={tableConfig}
          loading={loading || loadingConfig}
          toolbarCenterContent={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 220, minWidth: 220, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Ownership Type"
                  value={selectedOwnershipType}
                  onChange={handleLocationToolbarChange(
                    "organization_location_ownership_type_id"
                  )}
                  sx={selectSx}
                >
                  <MenuItem value="All">All</MenuItem>
                  {ownershipTypeOptions.map((type) => (
                    <MenuItem key={String(type.id)} value={String(type.id)}>
                      {type.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 180, minWidth: 180, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="City"
                  value={selectedCity}
                  onChange={handleLocationToolbarChange("general_city_id")}
                  sx={selectSx}
                >
                  <MenuItem value="All">All</MenuItem>
                  {cityOptions.map((city) => (
                    <MenuItem key={String(city.id)} value={String(city.id)}>
                      {city.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default OrganizationLocationList;
